import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { myId } from "../lib/me";
import { shortDate } from "../lib/weekLine";
import { lineCanSay, parseLine, saidOf, showLine } from "../lib/exerciseLine";
import {
  addExercise, applyLineMany, dropPayload, emptyTree, exercisesOf, findRow, lineFieldsOf, mondayOf, moveMany, pairWithNext,
  pairsAllowed, payloadOf, removeRow, runOf, sameExerciseElsewhere, sectionsOf, setsOf, swapExercise, tidy, unpair,
  type CreateEntry, type LibraryPick, type Landing, type TreePayload, type WeekTree,
} from "../lib/weekEdit";
import { wireNumber } from "../lib/wireNumber";
import { ExerciseSetWindow } from "./ExerciseSetWindow";
import { useTopLayerMenu } from "./useTopLayerMenu";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * WEEK EDITOR — STAGE 1 (2026-10-19, design A, `docs/WEEK-EDITOR-STAGE1-PLAN.md`): THE CLIENT'S EXTENDED WEEK IS EDITED IN
 * PLACE. William: *"I need to look at the whole week, and need to be able to edit the days easier. Move exercises from one day
 * to another, change little details if needed."* Ioannis starting the session was his yes; it ends the 2026-08-29 "both editors
 * are dimmed windows" rule for the CLIENT'S WEEK only (the Library builder stays a window).
 *
 * ONE provider per Extended week: it holds the trees as last written (drawn over the batched read until the read catches up),
 * the Undo / Redo stacks and the toast, and performs every change through ONE call (`write`, the page's `write_week_trees`).
 * Every change saves itself, live, with no extra notice to the client (William 10-19: the push he wants is the review's);
 * `Undo` in the toast and ⌘Z — never "are you sure?". A trained day is a record: no field, no `⋯` (`locked`).
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export type WeekWriteResult = { ok: true; updated: Readonly<Record<string, string>> } | { ok: false; said: string; stale?: boolean };
export type WeekWrite = (payloads: TreePayload[]) => Promise<WeekWriteResult>;

/** A visible day as the editor's menus name it: its workouts (untrained ones are targets). */
export type EditorDay = {
  dayKey: string;
  workouts: { workoutId: string; name: string; trained: boolean }[];
};

/** One change, as Undo needs it: each touched workout before and after (null = it did not exist / it was dropped). */
type Change = {
  label: string;
  before: Map<string, WeekTree | null>;
  after: Map<string, WeekTree | null>;
  creates: Map<string, CreateEntry>;
};

type Toast = { said: string; undo: boolean; alert: boolean; key: number };

type Editor = {
  treeOf: (workoutId: string) => WeekTree | undefined;
  locked: (workoutId: string) => boolean;
  days: readonly EditorDay[];
  dayOf: (workoutId: string) => string | undefined;
  /** Copies this editor created that the page's read has not listed yet — drawn at once on their day (no ~1 s gap). */
  pending: readonly { workoutId: string; dayKey: string; name: string }[];
  /** The row whose line is open (one at a time), and which one opens next after an add. */
  editing: string | null;
  setEditing: (rowId: string | null) => void;
  change: (label: string, after: Map<string, WeekTree | null>, creates?: Map<string, CreateEntry>) => Promise<boolean>;
  newId: () => string;
  library: () => Promise<LibraryPick[]>;
  openSets: (workoutId: string, rowId: string) => void;
  clientId: string;
  /* ── stage 2 ── */
  /** The picked rows (row id → its workout), across days. */
  selected: ReadonlyMap<string, string>;
  /** ⌘-click / `⋯ → Select` (toggle) and ⇧-click (range, within one workout); a superset member brings its superset. */
  pick: (workoutId: string, rowId: string, how: "toggle" | "range") => void;
  clearSelection: () => void;
  /** The selection by workout, the workouts in week order (day, then place in the day). */
  selectionPicks: () => { picks: Map<string, string[]>; order: string[] };
  /** The same library exercise on the week's other untrained days (rows one line can say). */
  sameElsewhere: (workoutId: string, rowId: string) => { workoutId: string; rowId: string; dayKey: string }[];
  /** A library WORKOUT assigned to a day (the panel's Workouts tab) — the page's `assign_coach_workout`; its sentence toasts. */
  assign: (w: { id: string; rootId: string; name: string }, dayKey: string, index: number) => void;
};

export type { Editor };
/** `write_week_trees` refuses more than 8 trees in one call (its SHAPE guard, migration 20261019120000). */
export const WEEK_WRITE_MAX = 8;
const EditorContext = createContext<Editor | null>(null);

/** The editor of the Week this component is drawn in, or null where the Week only reads (the Program board, a trained day). */
export function useWeekEditor(): Editor | null {
  return useContext(EditorContext);
}

const newUuid = (): string => (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
  ? crypto.randomUUID()
  : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => (Number(c) ^ (Math.random() * 16) >> (Number(c) / 4)).toString(16)));

function isTyping(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
}

export function WeekEditorProvider({ base, trained, days, clientId, write, onStructureChanged, onTreesStale, assignWorkout, children }: {
  /** Stage 2 — the page's assign of a library workout to a day; resolves to the sentence to show. */
  assignWorkout?: (w: { id: string; rootId: string; name: string }, dayKey: string, index: number) => Promise<{ said: string; ok: boolean }>;
  /** The batched read's trees (`useWeekTrees`), the truth once it catches up with a write. */
  base: ReadonlyMap<string, WeekTree>;
  /** Workouts whose day is a record (a logged set): read-only. */
  trained: ReadonlySet<string>;
  days: readonly EditorDay[];
  clientId: string;
  write: WeekWrite;
  /** A copy was created or dropped (a move to an empty day, or its Undo): the page re-reads the client's assignments. */
  onStructureChanged: () => void;
  /** Re-read the trees (after the set window saved, or a refusal for a stale token). */
  onTreesStale: () => void;
  children: ReactNode;
}) {
  /* The trees as WE last wrote them — drawn over `base` until `base` carries the same token (its re-read caught up). */
  const [written, setWritten] = useState<ReadonlyMap<string, WeekTree | null>>(new Map());
  useEffect(() => {
    setWritten((w) => {
      let changed = false;
      const next = new Map(w);
      for (const [id, t] of w) {
        const b = base.get(id);
        if (t !== null && b !== undefined && (b.updated_at ?? null) === (t.updated_at ?? null)) { next.delete(id); changed = true; }
        if (t === null && b === undefined) { next.delete(id); changed = true; }
      }
      return changed ? next : w;
    });
  }, [base]);
  const treeOf = useCallback((id: string): WeekTree | undefined => {
    if (written.has(id)) return written.get(id) ?? undefined;
    return base.get(id);
  }, [written, base]);

  const [undoStack, setUndo] = useState<Change[]>([]);
  const [redoStack, setRedo] = useState<Change[]>([]);
  const [toast, setToast] = useState<Toast | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [sets, setSets] = useState<{ workoutId: string; rowId: string } | null>(null);
  /* Copies created by a change (a move to an empty day), until the page lists them on their day. */
  const [created, setCreated] = useState<ReadonlyMap<string, { dayKey: string; name: string }>>(new Map());
  const busy = useRef(false);
  /* Stage 2 — the coach's picked rows (row → workout) and the last one picked (⇧-click's anchor). */
  const [selected, setSelected] = useState<ReadonlyMap<string, string>>(new Map());
  const anchor = useRef<{ workoutId: string; rowId: string } | null>(null);
  const [bulk, setBulk] = useState<"move" | "copy" | null>(null);

  const dayByWorkout = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of days) for (const w of d.workouts) m.set(w.workoutId, d.dayKey);
    return m;
  }, [days]);

  /** Send the trees in `after` (and the drops), with the tokens the CURRENT trees carry. */
  const send = useCallback(async (after: Map<string, WeekTree | null>, creates: Map<string, CreateEntry>, current: (id: string) => WeekTree | undefined): Promise<WeekWriteResult> => {
    const payloads: TreePayload[] = [];
    for (const [id, t] of after) {
      const now = current(id);
      if (t === null) { payloads.push(dropPayload(id, now?.updated_at ?? null)); continue; }
      if (now === undefined) {
        const c = creates.get(id);
        if (c === undefined) return { ok: false, said: "That workout is no longer on this week." };
        payloads.push(payloadOf(t, { create: c }));
      } else {
        payloads.push(payloadOf({ ...t, updated_at: now.updated_at ?? null }));
      }
    }
    return write(payloads);
  }, [write]);

  const perform = useCallback(async (c: Change, kind: "do" | "undo" | "redo"): Promise<boolean> => {
    if (busy.current) return false;
    busy.current = true;
    const target = kind === "undo" ? c.before : c.after;
    const shown = new Map(written);
    const current = (id: string) => (shown.has(id) ? shown.get(id) ?? undefined : base.get(id));
    /* Drawn at once (optimistic); put back on a refusal. */
    setWritten((w) => { const n = new Map(w); for (const [id, t] of target) n.set(id, t); return n; });
    const r = await send(target, c.creates, current);
    busy.current = false;
    if (!r.ok) {
      setWritten((w) => { const n = new Map(w); for (const id of target.keys()) { if (shown.has(id)) n.set(id, shown.get(id)!); else n.delete(id); } return n; });
      setToast({ said: r.said, undo: false, alert: true, key: Date.now() });
      if (r.stale === true) onTreesStale();
      return false;
    }
    /* The new tokens onto what was written, so the next change sends them. */
    const c_creates = (id: string) => c.creates.get(id);
    const stamped = new Map<string, WeekTree | null>();
    for (const [id, t] of target) stamped.set(id, t === null ? null : { ...t, updated_at: r.updated[id] ?? t.updated_at ?? null });
    setWritten((w) => { const n = new Map(w); for (const [id, t] of stamped) n.set(id, t); return n; });
    const done: Change = kind === "undo" ? { ...c, before: stamped } : { ...c, after: stamped };
    if (kind === "do") { setUndo((u) => [...u.slice(-49), done]); setRedo([]); }
    if (kind === "undo") { setUndo((u) => u.slice(0, -1)); setRedo((x) => [...x, done]); }
    if (kind === "redo") { setRedo((x) => x.slice(0, -1)); setUndo((u) => [...u, done]); }
    setToast({ said: kind === "undo" ? `Undone: ${c.label}` : c.label, undo: kind !== "undo", alert: false, key: Date.now() });
    setCreated((m) => {
      const n = new Map(m);
      for (const [id, t] of stamped) {
        const c = c_creates(id);
        if (t === null) n.delete(id); else if (c !== undefined) n.set(id, { dayKey: c.scheduled_date, name: c.name });
      }
      return n;
    });
    if ([...target.values()].some((t) => t === null) || [...target.keys()].some((id) => !base.has(id))) onStructureChanged();
    return true;
  }, [written, base, send, onStructureChanged, onTreesStale]);

  const change = useCallback(async (label: string, after: Map<string, WeekTree | null>, creates = new Map<string, CreateEntry>()) => {
    /* The one write takes at most 8 workouts (`write_week_trees`' SHAPE guard): a selection across more is said, never sent. */
    if (after.size > WEEK_WRITE_MAX) {
      setToast({ said: `That touches ${String(after.size)} workouts — pick rows from at most ${String(WEEK_WRITE_MAX - 1)} at a time.`, undo: false, alert: true, key: Date.now() });
      return false;
    }
    const before = new Map<string, WeekTree | null>();
    for (const id of after.keys()) before.set(id, treeOf(id) ?? null);
    return perform({ label, before, after, creates }, "do");
  }, [perform, treeOf]);

  const undo = useCallback(() => { const c = undoStack[undoStack.length - 1]; if (c !== undefined) void perform(c, "undo"); }, [undoStack, perform]);
  const redo = useCallback(() => { const c = redoStack[redoStack.length - 1]; if (c !== undefined) void perform(c, "redo"); }, [redoStack, perform]);

  /* ⌘Z / ⇧⌘Z (Ctrl on other systems) — outside a text field, where the browser's own undo belongs to the field. */
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "z" || isTyping(e.target)) return;
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
    };
    window.addEventListener("keydown", on);
    return () => { window.removeEventListener("keydown", on); };
  }, [undo, redo]);

  /* The toast stays 12 s — long enough to read it and reach Undo — and never leaves while the pointer or focus is on it. */
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (toast === null || held) return;
    const t = window.setTimeout(() => { setToast((x) => (x?.key === toast.key ? null : x)); }, 12000);
    return () => { window.clearTimeout(t); };
  }, [toast, held]);

  const libraryRead = useRef<Promise<LibraryPick[]> | null>(null);
  const library = useCallback(() => {
    libraryRead.current ??= (async () => {
      const r = await supabase.from("coach_exercises").select("id, name, notes").eq("coach_id", await myId()).is("hidden_at", null).order("name");
      if (r.error !== null) { libraryRead.current = null; return []; }
      return (r.data ?? []).map((x) => ({ id: x.id, name: x.name, note: x.notes ?? null }));
    })();
    return libraryRead.current;
  }, []);

  /* ── stage 2: the selection ── */
  const workoutOrder = useMemo(() => days.flatMap((d) => d.workouts.map((w) => w.workoutId)), [days]);
  const pick = useCallback((workoutId: string, rowId: string, how: "toggle" | "range") => {
    const t = treeOf(workoutId);
    if (t === undefined || trained.has(workoutId)) return;
    const flat = sectionsOf(t).flatMap(exercisesOf).map((e) => e.id);
    let ids = runOf(t, rowId);
    const a = anchor.current;
    if (how === "range" && a !== null && a.workoutId === workoutId && flat.includes(a.rowId)) {
      const [i, j] = [flat.indexOf(a.rowId), flat.indexOf(rowId)].sort((x, y) => x - y);
      ids = [...new Set(flat.slice(i, j! + 1).flatMap((id) => runOf(t, id)))];
      setSelected((m) => { const n = new Map(m); for (const id of ids) n.set(id, workoutId); return n; });
    } else {
      setSelected((m) => {
        const n = new Map(m);
        const on = !n.has(rowId);
        for (const id of ids) { if (on) n.set(id, workoutId); else n.delete(id); }
        return n;
      });
    }
    anchor.current = { workoutId, rowId };
  }, [treeOf, trained]);
  const clearSelection = useCallback(() => { setSelected(new Map()); setBulk(null); anchor.current = null; }, []);
  /* A picked row that is gone (moved by Undo, removed, its day now trained) leaves the selection. */
  useEffect(() => {
    setSelected((m) => {
      let changed = false;
      const n = new Map(m);
      for (const [rowId, wid] of m) {
        const t = treeOf(wid);
        if (t === undefined || trained.has(wid) || findRow(t, rowId) === null) { n.delete(rowId); changed = true; }
      }
      return changed ? n : m;
    });
  }, [treeOf, trained]);
  const selectionPicks = useCallback(() => {
    const picks = new Map<string, string[]>();
    for (const [rowId, wid] of selected) picks.set(wid, [...(picks.get(wid) ?? []), rowId]);
    const order = [...picks.keys()].sort((x, y) => {
      const i = workoutOrder.indexOf(x), j = workoutOrder.indexOf(y);
      return (i === -1 ? 1e9 : i) - (j === -1 ? 1e9 : j);
    });
    return { picks, order };
  }, [selected, workoutOrder]);
  /* Escape lets the selection go (outside a field and an open menu, which take Escape first). */
  useEffect(() => {
    if (selected.size === 0) return;
    const on = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented && !isTyping(e.target)) clearSelection(); };
    window.addEventListener("keydown", on);
    return () => { window.removeEventListener("keydown", on); };
  }, [selected.size, clearSelection]);

  const sameElsewhere = useCallback((workoutId: string, rowId: string) => {
    const t = treeOf(workoutId);
    const day = dayByWorkout.get(workoutId);
    if (t === undefined || day === undefined) return [];
    const at = findRow(t, rowId);
    const row = at === null ? undefined : exercisesOf(at.section)[at.index];
    if (row === undefined) return [];
    const monday = mondayOf(day);
    const candidates = days.filter((d) => mondayOf(d.dayKey) === monday)
      .flatMap((d) => d.workouts.filter((w) => !w.trained).map((w) => ({ workoutId: w.workoutId, dayKey: d.dayKey, tree: treeOf(w.workoutId) })))
      .filter((c): c is { workoutId: string; dayKey: string; tree: WeekTree } => c.tree !== undefined);
    return sameExerciseElsewhere(candidates, row.exercise_id, rowId, showLine(lineFieldsOf(row)), rowSayable);
  }, [treeOf, dayByWorkout, days]);

  const listed = new Set(days.flatMap((d) => d.workouts.map((w) => w.workoutId)));
  const pending = [...created].filter(([id]) => !listed.has(id) && written.get(id) != null)
    .map(([workoutId, c]) => ({ workoutId, dayKey: c.dayKey, name: c.name }));
  const editor: Editor = {
    treeOf, locked: (id) => trained.has(id), days, pending,
    dayOf: (id) => dayByWorkout.get(id) ?? created.get(id)?.dayKey, editing, setEditing, change,
    newId: newUuid, library, openSets: (workoutId, rowId) => { setSets({ workoutId, rowId }); }, clientId,
    selected, pick, clearSelection, selectionPicks, sameElsewhere,
    assign: (w, dayKey, index) => {
      if (assignWorkout === undefined) return;
      void assignWorkout(w, dayKey, index).then((r) => { setToast({ said: r.said, undo: false, alert: !r.ok, key: Date.now() }); });
    },
  };

  return (
    <EditorContext.Provider value={editor}>
      {children}
      {toast !== null && (
        <div className={selected.size > 0 ? "pending-toast week-toast is-raised" : "pending-toast week-toast"} role={toast.alert ? "alert" : "status"} aria-live="polite"
             onPointerEnter={() => { setHeld(true); }} onPointerLeave={() => { setHeld(false); }}
             onFocus={() => { setHeld(true); }} onBlur={() => { setHeld(false); }}>
          <span>{toast.said}</span>
          {toast.undo && undoStack.length > 0 && (
            <button type="button" className="ht-button ht-button-quiet pending-undo" onClick={undo}>Undo</button>
          )}
        </div>
      )}
      {selected.size > 0 && <SelectionBar bulk={bulk} setBulk={setBulk} />}
      {sets !== null && (
        <ExerciseSetWindow workoutId={sets.workoutId} exerciseRowId={sets.rowId}
          onClose={() => { setSets(null); }}
          onSaved={() => { setWritten((w) => { const n = new Map(w); n.delete(sets.workoutId); return n; }); onTreesStale(); }} />
      )}
    </EditorContext.Provider>
  );
}

/** Can one line say this row's sets (no per-set rests, no rounds)? Else its line opens the set table. */
function rowSayable(e: { coach_workout_sets?: unknown }): boolean {
  return lineCanSay(setsOf(e as Parameters<typeof setsOf>[0]).map((s) => ({
    restSeconds: s.rest_seconds, rounds: s.rounds, timeSeconds: s.time_seconds, distanceM: wireNumber(s.distance_m ?? null),
  })));
}

/* ── the row's one line ───────────────────────────────────────────────────────────────────────────────────────────── */

function rowOf(t: WeekTree | undefined, rowId: string) {
  if (t === undefined) return undefined;
  const at = findRow(t, rowId);
  return at === null ? undefined : exercisesOf(at.section)[at.index];
}

function rowName(t: WeekTree | undefined, rowId: string): string {
  return rowOf(t, rowId)?.coach_exercises?.name ?? "Exercise";
}

/**
 * THE LINE. At rest it is what the column always drew (dose · rest · note) inside a quiet button; pressed, it is a field holding
 * the row as one line (`showLine`), and under it what the line will write (`said`) — before anything is saved. Enter or leaving
 * the field saves; Escape puts it back. A row the line cannot say (per-set rests, rounds) opens the set table instead.
 */
export function LineField({ workoutId, rowId, drawn }: { workoutId: string; rowId: string; drawn: ReactNode }) {
  const ed = useWeekEditor();
  const t = ed?.treeOf(workoutId);
  const row = rowOf(t, rowId);
  const initial = useMemo(() => (row === undefined ? "" : showLine(lineFieldsOf(row))), [row]);
  const [text, setText] = useState(initial);
  const [everywhere, setEverywhere] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);
  const open = ed !== null && ed.editing === rowId;
  useEffect(() => { if (open) { setText(initial); setEverywhere(false); requestAnimationFrame(() => { const el = field.current; if (el !== null) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); } }); } }, [open, initial]);
  useEffect(() => {
    const el = field.current;
    if (el === null) return;
    el.style.height = "auto";
    el.style.height = `${String(el.scrollHeight)}px`;
  }, [text, open]);
  if (ed === null || row === undefined || ed.locked(workoutId)) return <>{drawn}</>;
  const sayable = rowSayable(row);
  const name = row.coach_exercises?.name ?? "exercise";
  if (!open) {
    return (
      <button type="button" className="week-line" aria-label={`Edit ${name}: ${initial || "no sets"}`} title="Edit"
              onClick={() => { if (sayable) ed.setEditing(rowId); else ed.openSets(workoutId, rowId); }}>
        {drawn}
      </button>
    );
  }
  const parsed = parseLine(text);
  /* Stage 2 — "change everywhere this week": the same exercise on the week's other untrained days, written only when ticked. */
  const others = ed.sameElsewhere(workoutId, rowId);
  function commit() {
    if (ed === null || t === undefined) return;
    ed.setEditing(null);
    const unchanged = text.trim() === initial.trim();
    if (text.trim() === "" || (unchanged && !everywhere)) return;
    const rows = [...(unchanged ? [] : [{ workoutId, rowId }]), ...(everywhere ? others : [])];
    const fields = unchanged ? lineFieldsOf(row!) : parsed;
    const days = new Set([...(unchanged ? [] : [ed.dayOf(workoutId)]), ...(everywhere ? others.map((o) => o.dayKey) : [])]).size;
    void ed.change(days > 1 ? `${name} changed on ${String(days)} days` : `${name} changed`, applyLineMany(ed.treeOf, rows, fields, ed.newId) as Map<string, WeekTree | null>);
  }
  return (
    <span className="week-line-edit"
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commit(); }}>
      <textarea ref={field} className="week-line-input" rows={1} value={text} spellCheck={false}
                aria-label={`${name}: sets, reps, load, tempo, rest, note`}
                aria-describedby={`said-${rowId}`}
                onChange={(e) => { setText(e.target.value); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); }
                  if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); ed.setEditing(null); }
                }} />
      <span className="week-line-said" id={`said-${rowId}`} aria-live="polite">{saidOf(parsed).join(" · ")}</span>
      {others.length > 0 && (
        <label className="week-line-everywhere">
          <input type="checkbox" checked={everywhere} onChange={(e) => { setEverywhere(e.currentTarget.checked); }}
                 onKeyDown={(e) => {
                   if (e.key === "Enter") { e.preventDefault(); commit(); }
                   if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); ed.setEditing(null); }
                 }} />
          <span>Same on {[...new Set(others.map((o) => shortDate(o.dayKey)))].join(" · ")}</span>
        </label>
      )}
      <button type="button" className="week-line-sets"
              onMouseDown={(e) => { e.preventDefault(); }}
              onClick={() => { ed.setEditing(null); ed.openSets(workoutId, rowId); }}>Sets…</button>
    </span>
  );
}

/* ── the row's ⋯ ──────────────────────────────────────────────────────────────────────────────────────────────────── */

type MenuMode =
  | { kind: "root" }
  | { kind: "targets"; rows: string[]; copy: boolean; what: string }
  | { kind: "swap" };

export function RowMenu({ workoutId, rowId }: { workoutId: string; rowId: string }) {
  const ed = useWeekEditor();
  const [mode, setMode] = useState<MenuMode | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useTopLayerMenu(mode !== null, trigger, list);
  useEffect(() => {
    if (mode === null) return;
    list.current?.querySelector<HTMLElement>("button, input")?.focus();
    const away = (e: PointerEvent) => {
      if (list.current?.contains(e.target as Node) === true || trigger.current?.contains(e.target as Node) === true) return;
      setMode(null);
    };
    document.addEventListener("pointerdown", away, true);
    return () => { document.removeEventListener("pointerdown", away, true); };
  }, [mode]);
  if (ed === null || ed.locked(workoutId)) return null;
  const t = ed.treeOf(workoutId);
  if (t === undefined) return null;
  const at = findRow(t, rowId);
  if (at === null) return null;
  const run = runOf(t, rowId);
  const inRun = run.length > 1;
  const name = rowName(t, rowId);
  const rows = exercisesOf(at.section);
  const lastOfRun = rows.findIndex((r) => r.id === run[run.length - 1]);
  const canPair = pairsAllowed(at.section.format) && lastOfRun < rows.length - 1;
  const groupWords = inRun ? run.map((id) => rowName(t, id)).join(" + ") : name;

  /* Focus goes back to the `⋯` WITHOUT scrolling: after a move that `⋯` lives on another day, and a focus that scrolled its
     column would hide the workout's head (seen on the first stills). */
  function close(focus: boolean) { setMode(null); if (focus) trigger.current?.focus({ preventScroll: true }); }
  function act(label: string, after: WeekTree | null) {
    close(true);
    if (ed === null || after === null) return;
    void ed.change(label, new Map([[workoutId, after]]));
  }
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.stopPropagation(); if (mode?.kind === "root") close(true); else setMode({ kind: "root" }); return; }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const items = [...(list.current?.querySelectorAll<HTMLElement>("button") ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    e.preventDefault();
    items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
  }

  return (
    <span className="assignment-menu week-row-menu" onKeyDown={onKeyDown}>
      <button ref={trigger} type="button" className="assignment-menu-trigger week-row-more" aria-haspopup="menu"
              aria-expanded={mode !== null} aria-label={`Actions for ${name}`}
              onClick={() => { setMode(mode === null ? { kind: "root" } : null); }}>⋯</button>
      {mode !== null && (
        <div ref={list} className="assignment-menu-list is-top-layer week-row-list" role="menu" aria-label={`Actions for ${name}`}>
          {mode.kind === "root" && (
            <>
              <MenuButton onClick={() => { close(true); ed.pick(workoutId, rowId, "toggle"); }}>
                {ed.selected.has(rowId) ? "Deselect" : "Select"}
              </MenuButton>
              <MenuButton onClick={() => { setMode({ kind: "targets", rows: run, copy: false, what: groupWords }); }}>
                {inRun ? "Move superset to…" : "Move to…"}
              </MenuButton>
              {inRun && (
                <MenuButton onClick={() => { setMode({ kind: "targets", rows: [rowId], copy: false, what: name }); }}>Move just this exercise…</MenuButton>
              )}
              <MenuButton onClick={() => { setMode({ kind: "targets", rows: inRun ? run : [rowId], copy: true, what: groupWords }); }}>
                {inRun ? "Copy superset to…" : "Copy to…"}
              </MenuButton>
              <MenuButton onClick={() => { setMode({ kind: "swap" }); }}>Swap exercise…</MenuButton>
              {canPair && (
                <MenuButton onClick={() => { act(`${groupWords} + ${rowName(t, rows[lastOfRun + 1]?.id ?? "")} paired`, pairWithNext(t, rowId, ed.newId)); }}>Superset with next</MenuButton>
              )}
              {inRun && <MenuButton onClick={() => { act(`${groupWords} unpaired`, unpair(t, rowId)); }}>Unpair</MenuButton>}
              <MenuButton onClick={() => { close(true); ed.openSets(workoutId, rowId); }}>Sets…</MenuButton>
              <MenuButton destructive onClick={() => { act(`${name} removed`, removeRow(t, rowId)); }}>Remove</MenuButton>
            </>
          )}
          {mode.kind === "targets" && (
            <Targets exclude={mode.copy ? undefined : workoutId} title={`${mode.copy ? "Copy" : "Move"} ${mode.what} to`} newName={t.name}
                     go={(to) => {
                       close(true);
                       landRows(ed, new Map([[workoutId, mode.rows]]), [workoutId], to, mode.copy, mode.what);
                     }} />
          )}
          {mode.kind === "swap" && (
            <LibrarySearch label={`Swap ${name} for`} onPick={(pick) => { act(`${name} swapped for ${pick.name}`, swapExercise(t, rowId, pick)); }} />
          )}
        </div>
      )}
    </span>
  );
}

export function MenuButton({ children, onClick, destructive = false }: { children: ReactNode; onClick: () => void; destructive?: boolean }) {
  return (
    <button type="button" role="menuitem" className={destructive ? "assignment-menu-item is-destructive" : "assignment-menu-item"} onClick={onClick}>
      {children}
    </button>
  );
}

/** A place rows (or a library pick) can go: an untrained workout on a visible day, or a new one on an empty day. */
export type TargetChoice = { workoutId: string; dayKey: string; tree: WeekTree | undefined; name: string; create?: CreateEntry };

/** Rows from one or several copies moved (or copied) to the END of a chosen workout, as ONE change and one Undo. */
export function landRows(ed: Editor, picks: ReadonlyMap<string, readonly string[]>, order: readonly string[], to: TargetChoice,
                         copy: boolean, what: string): void {
  const target = to.tree ?? emptyTree(to.workoutId, to.name);
  const landing: Landing = { workoutId: to.workoutId };
  const sources = order.map((id) => ed.treeOf(id)).filter((x): x is WeekTree => x !== undefined);
  const after = moveMany(sources, picks, target, landing, copy, ed.newId) as Map<string, WeekTree | null>;
  const creates = new Map<string, CreateEntry>();
  if (to.create !== undefined) creates.set(to.workoutId, to.create);
  if (!copy) ed.clearSelection();
  void ed.change(`${what} ${copy ? "copied" : "moved"} to ${shortDate(to.dayKey)}`, after, creates);
}

/** The days a row can go to: every visible day's untrained workouts (but `exclude`), and a new workout on a day that has none. */
export function Targets({ exclude, title, newName, go }: { exclude?: string; title: string; newName: string; go: (to: TargetChoice) => void }) {
  const ed = useWeekEditor();
  if (ed === null) return null;
  const items: ReactNode[] = [];
  for (const d of ed.days) {
    const open = d.workouts.filter((w) => !w.trained && w.workoutId !== exclude);
    for (const w of open) {
      items.push(
        <MenuButton key={w.workoutId} onClick={() => { go({ workoutId: w.workoutId, dayKey: d.dayKey, tree: ed.treeOf(w.workoutId), name: w.name }); }}>
          {shortDate(d.dayKey)} · {w.name}
        </MenuButton>,
      );
    }
    if (d.workouts.length === 0) {
      items.push(
        <MenuButton key={`new-${d.dayKey}`} onClick={() => {
          const id = ed.newId();
          go({ workoutId: id, dayKey: d.dayKey, tree: undefined, name: newName,
               create: { assignment_id: ed.newId(), client_id: ed.clientId, scheduled_date: d.dayKey, name: newName } });
        }}>
          {shortDate(d.dayKey)} · <span className="week-row-new">new workout</span>
        </MenuButton>,
      );
    }
  }
  return (
    <>
      <p className="week-row-list-head">{title}</p>
      {items.length === 0 ? <p className="week-row-list-head">No other day on this week can take it.</p> : items}
    </>
  );
}

/* ── stage 2: the bar while rows are picked ───────────────────────────────────────────────────────────────────────── */

/** `3 selected · Move to… · Copy to… · Remove · Clear` at the window's foot (the Exercises page's selection line, as a bar). */
function SelectionBar({ bulk, setBulk }: { bulk: "move" | "copy" | null; setBulk: (b: "move" | "copy" | null) => void }) {
  const ed = useWeekEditor();
  const anchorMove = useRef<HTMLButtonElement>(null);
  const anchorCopy = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const trigger = bulk === "copy" ? anchorCopy : anchorMove;
  useTopLayerMenu(bulk !== null, trigger, list);
  useEffect(() => {
    if (bulk === null) return;
    list.current?.querySelector<HTMLElement>("button")?.focus();
    const away = (e: PointerEvent) => {
      if (list.current?.contains(e.target as Node) === true || trigger.current?.contains(e.target as Node) === true) return;
      setBulk(null);
    };
    document.addEventListener("pointerdown", away, true);
    return () => { document.removeEventListener("pointerdown", away, true); };
  }, [bulk, setBulk, trigger]);
  if (ed === null) return null;
  const { picks, order } = ed.selectionPicks();
  const n = ed.selected.size;
  const what = n === 1 ? "1 exercise" : `${String(n)} exercises`;
  const first = ed.treeOf(order[0] ?? "");
  function remove() {
    if (ed === null) return;
    const after = new Map<string, WeekTree | null>();
    for (const [wid, ids] of picks) {
      let t = ed.treeOf(wid);
      if (t === undefined) continue;
      for (const id of ids) t = removeRow(t, id);
      after.set(wid, tidy(t));
    }
    ed.clearSelection();
    void ed.change(`${what} removed`, after);
  }
  return (
    <div className="week-select-bar" role="toolbar" aria-label="Selected exercises"
         onKeyDown={(e) => { if (e.key === "Escape" && bulk !== null) { e.stopPropagation(); e.preventDefault(); setBulk(null); trigger.current?.focus(); } }}>
      <strong>{n} selected</strong>
      <button ref={anchorMove} type="button" className="ours-text-action" aria-haspopup="menu" aria-expanded={bulk === "move"}
              onClick={() => { setBulk(bulk === "move" ? null : "move"); }}>Move to…</button>
      <button ref={anchorCopy} type="button" className="ours-text-action" aria-haspopup="menu" aria-expanded={bulk === "copy"}
              onClick={() => { setBulk(bulk === "copy" ? null : "copy"); }}>Copy to…</button>
      <button type="button" className="ours-text-action" onClick={remove}>Remove</button>
      <button type="button" className="ours-text-action" onClick={() => { ed.clearSelection(); }}>Clear</button>
      {bulk !== null && (
        <div ref={list} className="assignment-menu-list is-top-layer week-row-list" role="menu" aria-label={`${bulk === "copy" ? "Copy" : "Move"} ${what} to`}>
          <Targets title={`${bulk === "copy" ? "Copy" : "Move"} ${what} to`} newName={first?.name ?? "Workout"}
                   exclude={bulk === "move" && order.length === 1 ? order[0] : undefined}
                   go={(to) => { setBulk(null); landRows(ed, picks, order, to, bulk === "copy", what); }} />
        </div>
      )}
    </div>
  );
}

/** The coach's library, searched as you type; Enter takes the first match. */
function LibrarySearch({ label, onPick }: { label: string; onPick: (p: LibraryPick) => void }) {
  const ed = useWeekEditor();
  const [all, setAll] = useState<LibraryPick[] | null>(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    let cancelled = false;
    void ed?.library().then((l) => { if (!cancelled) setAll(l); });
    return () => { cancelled = true; };
  }, [ed]);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hits = (all ?? []).filter((p) => words.every((w) => p.name.toLowerCase().includes(w))).slice(0, 8);
  return (
    <div className="week-search">
      <input className="ht-input week-search-input" value={q} placeholder="Search your library" aria-label={label}
             onChange={(e) => { setQ(e.target.value); }}
             onKeyDown={(e) => { if (e.key === "Enter" && hits[0] !== undefined) { e.preventDefault(); onPick(hits[0]); } }} />
      {all === null && <p className="week-row-list-head">Loading…</p>}
      {all !== null && hits.length === 0 && <p className="week-row-list-head">No exercise matches.</p>}
      {hits.map((p) => <MenuButton key={p.id} onClick={() => { onPick(p); }}>{p.name}</MenuButton>)}
    </div>
  );
}

/* ── + Exercise under a workout ───────────────────────────────────────────────────────────────────────────────────── */

export function AddExercise({ workoutId }: { workoutId: string }) {
  const ed = useWeekEditor();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useTopLayerMenu(open, trigger, list);
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>("input")?.focus();
    const away = (e: PointerEvent) => {
      if (list.current?.contains(e.target as Node) === true || trigger.current?.contains(e.target as Node) === true) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", away, true);
    return () => { document.removeEventListener("pointerdown", away, true); };
  }, [open]);
  if (ed === null || ed.locked(workoutId)) return null;
  const t = ed.treeOf(workoutId);
  if (t === undefined) return null;
  return (
    <div className="week-add">
      <button ref={trigger} type="button" className="week-add-button" aria-expanded={open} aria-haspopup="dialog"
              onClick={() => { setOpen(!open); }}>+ Exercise</button>
      {open && (
        <div ref={list} className="assignment-menu-list is-top-layer week-row-list" role="dialog" aria-label="Add an exercise"
             onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); trigger.current?.focus(); } }}>
          <LibrarySearch label={`Add an exercise to ${t.name}`} onPick={(pick) => {
            setOpen(false);
            const { tree, rowId } = addExercise(t, pick, ed.newId);
            void ed.change(`${pick.name} added`, new Map([[workoutId, tree]])).then((ok) => { if (ok) ed.setEditing(rowId); });
          }} />
        </div>
      )}
    </div>
  );
}
